import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Avatar,
  BoltIcon,
  Button,
  Card,
  Chip,
  InstantBadge,
  LeafIcon,
  MAP_ROUTES,
  MapRouteChip,
  PawIcon,
  Price,
  RangeSlider,
  RideTimeColumn,
  SchematicMap,
  SearchIcon,
  SelectChip,
  Sheet,
  SmolitMark,
  SortIcon,
  Toggle,
  VerifiedBadgeIcon,
  WahineIcon,
  cn,
} from '@openroad/ui';
import {
  api,
  formatCo2,
  formatDuration,
  formatMoney,
  formatTime,
  todayInNewZealand,
  unwrap,
} from '../lib/api';
import { useIsMobileLayout } from '../lib/viewport';
import { useSmolit } from '../lib/useSmolit';

const WINDOWS = [
  ['any', 'Any time'],
  ['morning', 'Morning'],
  ['afternoon', 'Afternoon'],
  ['evening', 'Evening'],
] as const;

const RATINGS = [
  ['any', 'Any'],
  ['4.5', '4.5+'],
  ['4.8', '4.8+'],
] as const;

const SORTS = {
  best: 'Best match',
  price: 'Lowest price',
  time: 'Fastest',
  rating: 'Top rated',
} as const;

type Sort = keyof typeof SORTS;
type Ride = NonNullable<Awaited<ReturnType<typeof searchTrips>>>['items'][number];

function searchTrips(query: Record<string, string>) {
  return unwrap(api.GET('/trips/search', { params: { query: query as never } }));
}

interface Filters {
  maxPriceDollars: number;
  window: (typeof WINDOWS)[number][0];
  minRating: (typeof RATINGS)[number][0];
  instantBook: boolean;
  womenOnly: boolean;
  petsAllowed: boolean;
}

const DEFAULT_FILTERS: Filters = {
  maxPriceDollars: 60,
  window: 'any',
  minRating: 'any',
  instantBook: false,
  womenOnly: false,
  petsAllowed: false,
};

/** How many refinements are on, so a hidden filter rail can still say so. */
function activeFilterCount(filters: Filters): number {
  return (
    (filters.maxPriceDollars !== DEFAULT_FILTERS.maxPriceDollars ? 1 : 0) +
    (filters.window !== 'any' ? 1 : 0) +
    (filters.minRating !== 'any' ? 1 : 0) +
    (filters.instantBook ? 1 : 0) +
    (filters.womenOnly ? 1 : 0) +
    (filters.petsAllowed ? 1 : 0)
  );
}

/**
 * The filter controls, in the desktop rail and in the mobile sheet.
 *
 * One set of controls in one place: a phone that filtered by a different set of
 * chips from the desktop would be a second product. The rail supplies the card
 * around them and the sheet supplies the sheet; neither owns the controls.
 */
function FilterControls({
  filters,
  onChange,
}: {
  filters: Filters;
  onChange: (next: Partial<Filters>) => void;
}) {
  return (
    <>
      <RangeSlider
        className="mb-[18px]"
        value={filters.maxPriceDollars}
        onValueChange={(value) => onChange({ maxPriceDollars: value })}
        min={5}
        max={80}
        label="Max price"
        valueLabel={`$${filters.maxPriceDollars}`}
      />

      <div className="mb-[18px]">
        <span className="mb-2.5 block text-label font-semibold">Departure window</span>
        <div className="grid grid-cols-2 gap-[7px]">
          {WINDOWS.map(([key, label]) => (
            <SelectChip
              key={key}
              selected={filters.window === key}
              onClick={() => onChange({ window: key })}
            >
              {label}
            </SelectChip>
          ))}
        </div>
      </div>

      <div className="mb-[18px]">
        <span className="mb-2.5 block text-label font-semibold">Minimum rating</span>
        <div className="flex gap-[7px]">
          {RATINGS.map(([key, label]) => (
            <SelectChip
              key={key}
              selected={filters.minRating === key}
              onClick={() => onChange({ minRating: key })}
            >
              {label}
            </SelectChip>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-0.5">
        <Toggle
          checked={filters.instantBook}
          onCheckedChange={(value) => onChange({ instantBook: value })}
          tone="pohutukawa"
          label="Instant book"
          icon={<BoltIcon size={15} className="text-pohutukawa" />}
        />
        <Toggle
          checked={filters.womenOnly}
          onCheckedChange={(value) => onChange({ womenOnly: value })}
          tone="wahine"
          label="Women only"
          icon={<WahineIcon size={15} className="text-wahine" />}
        />
        <Toggle
          checked={filters.petsAllowed}
          onCheckedChange={(value) => onChange({ petsAllowed: value })}
          label="Pets allowed"
          icon={<PawIcon size={15} className="text-muted" />}
        />
      </div>
    </>
  );
}

/**
 * One search result.
 *
 * Three columns on a wide screen, as the export draws it. On a phone the price
 * column wraps to a line of its own and lays itself out sideways, because three
 * columns do not fit: at 387px the row came to 333px inside a 316px content box
 * and spilled past the card's own padding, putting the price and "17 kg saved"
 * flush against the border. Measured on a OnePlus 7T Pro — it never showed as
 * page overflow, because the card absorbed it.
 *
 * Squeezing the middle column instead was tried and was worse. It fits by
 * truncating, and the first thing to go is the driver's name: "H…" above a
 * price is not a ride anybody can choose. Wrapping keeps every word and costs
 * one line (ADR-0010: layout may change on a phone, values may not).
 */
function RideResultCard({ ride, onOpen }: { ride: Ride; onOpen: () => void }) {
  return (
    <Card lift="ride" onClick={onOpen}>
      <div className="flex gap-4 max-md:flex-wrap max-md:gap-x-3 max-md:gap-y-3">
        <RideTimeColumn
          departure={formatTime(ride.departureAt)}
          arrival={formatTime(ride.arrivalAt)}
          duration={formatDuration(ride.durationSeconds)}
        />

        <div className="min-w-0 flex-1 border-l border-line-soft pl-4 max-md:pl-3">
          <div className="mb-2.5 flex items-center gap-2.5">
            <Avatar
              initials={ride.driver.initials}
              colour={ride.driver.avatarColour}
              size={40}
              name={ride.driver.displayName}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-[7px]">
                <span className="text-body-lg font-bold">{ride.driver.displayName}</span>
                {ride.driver.verifiedBadges.includes('ID') ? (
                  <VerifiedBadgeIcon size={14} className="flex-none" />
                ) : null}
              </div>
              <div className="mt-px text-meta text-muted">
                <span className="font-bold text-ngahere">★ {ride.driver.ratingAverage ?? '—'}</span>{' '}
                · {ride.driver.reviewCount} {ride.driver.reviewCount === 1 ? 'review' : 'reviews'}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {ride.chattiness === 'QUIET' ? <Chip>Quiet ride</Chip> : null}
            {ride.chattiness === 'CHATTY' ? <Chip>Happy to chat</Chip> : null}
            {ride.musicWelcome ? <Chip>Music ok</Chip> : null}
            {ride.petsAllowed ? <Chip>Pets ok</Chip> : null}
            {ride.womenOnly ? <Chip>Women only</Chip> : null}
            {!ride.smokingAllowed ? <Chip>No smoking</Chip> : null}
          </div>
        </div>

        <div
          className={cn(
            'flex flex-col items-end justify-between text-right md:min-w-[96px]',
            // A full-width footer row on a phone: price on the left, the badges
            // and figures on the right, on the one line the wrap bought.
            'max-md:w-full max-md:flex-row max-md:items-center max-md:justify-between',
            'max-md:border-t max-md:border-line-soft max-md:pt-3',
          )}
        >
          <Price amount={formatMoney(ride.pricePerSeat)} caption="per seat" />
          <div className="flex flex-col items-end gap-1.5 max-md:flex-row max-md:items-center max-md:gap-2.5">
            {ride.instantBook ? <InstantBadge /> : null}
            <span className="flex items-center gap-1 text-mini whitespace-nowrap text-muted">
              <LeafIcon size={13} withStem={false} className="text-leaf" />
              {formatCo2(ride.co2SavedGramsPerSeat)} saved
            </span>
            <span className="text-mini whitespace-nowrap text-subtle">
              {ride.seatsAvailable} {ride.seatsAvailable === 1 ? 'seat' : 'seats'} left
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}

/** The list itself, with its three empty states. */
function RideResults({
  rides,
  pending,
  failed,
  onOpen,
}: {
  rides: Ride[];
  pending: boolean;
  failed: boolean;
  onOpen: (id: string) => void;
}) {
  if (pending) return <p className="text-body text-muted">Finding rides…</p>;

  if (failed) {
    return (
      <Card radius="panel" className="p-6">
        <p className="m-0 text-body text-muted">
          We could not load rides just now. Try again in a moment.
        </p>
      </Card>
    );
  }

  if (rides.length === 0) {
    return (
      <Card radius="panel" className="p-6">
        <p className="m-0 mb-1 text-body-lg font-semibold">No rides match those filters</p>
        <p className="m-0 text-body text-muted">
          Try widening the price, the departure window, or the date.
        </p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {rides.map((ride) => (
        <RideResultCard key={ride.id} ride={ride} onOpen={() => onOpen(ride.id)} />
      ))}
    </div>
  );
}

export function SearchScreen() {
  const navigate = useNavigate();
  const smolit = useSmolit();
  const mobile = useIsMobileLayout();
  const [params, setParams] = useSearchParams();

  const from = params.get('from') ?? 'Auckland';
  const to = params.get('to') ?? 'Tauranga';
  const date = params.get('date') ?? todayInNewZealand();
  const seats = Number(params.get('seats') ?? 1);

  // Filters live in component state rather than the URL: the design's filter
  // rail is a refinement of a search, not a new one, and putting six more keys
  // in the address bar would make the shareable link unreadable.
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [sort, setSort] = useState<Sort>('best');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const changeFilters = (next: Partial<Filters>) =>
    setFilters((current) => ({ ...current, ...next }));

  const query = useMemo(() => {
    const search: Record<string, string> = {
      from,
      to,
      date,
      seats: String(seats),
      sort,
      window: filters.window,
      maxPriceCents: String(filters.maxPriceDollars * 100),
    };
    if (filters.minRating !== 'any') search.minRating = filters.minRating;
    // Only send a boolean when it is on. Sending `false` would ask the API to
    // filter for rides that are explicitly not instant-book, which is not what
    // an unticked box means.
    if (filters.instantBook) search.instantBook = 'true';
    if (filters.womenOnly) search.womenOnly = 'true';
    if (filters.petsAllowed) search.petsAllowed = 'true';
    return search;
  }, [from, to, date, seats, sort, filters]);

  const results = useQuery({
    queryKey: ['trips', query],
    queryFn: () => searchTrips(query),
  });

  function updateSearch(next: Partial<Record<string, string>>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === undefined) merged.delete(key);
      else merged.set(key, value);
    }
    setParams(merged, { replace: true });
  }

  function cycleSort() {
    const order: Sort[] = ['best', 'price', 'time', 'rating'];
    setSort(order[(order.indexOf(sort) + 1) % order.length]!);
  }

  const total = results.data?.total ?? 0;
  const filterCount = activeFilterCount(filters);

  const rideList = (
    <RideResults
      rides={results.data?.items ?? []}
      pending={results.isPending}
      failed={results.isError}
      onOpen={(id) => navigate(`/trips/${id}`)}
    />
  );

  const sortButton = (
    <button
      type="button"
      onClick={cycleSort}
      className="flex cursor-pointer items-center gap-[7px] rounded-lg border border-line bg-surface px-3 py-2 text-label font-semibold text-ink"
    >
      <SortIcon size={15} className="text-muted" /> {SORTS[sort]}
    </button>
  );

  /* ----------------------------------------------------------------- mobile */
  if (mobile) {
    return (
      <main>
        {/* The same four inputs as the desktop bar, in two rows instead of one.
            A single 680px pill does not survive a 390px screen.

            It scrolls away rather than sticking: the bar that has to stay
            reachable while reading results is the one with Sort and Filters on
            it, and two stacked sticky bars would leave a phone showing more
            chrome than rides. */}
        <div className="border-b border-line bg-surface px-4 py-2.5">
          <div className="flex items-center gap-2">
            <div className="flex flex-1 items-center overflow-hidden rounded-2xl border border-line bg-sunk">
              <input
                value={from}
                onChange={(event) => updateSearch({ from: event.target.value })}
                aria-label="Leaving from"
                className="w-0 flex-1 border-none bg-transparent px-3 py-[11px] text-body font-semibold outline-none"
              />
              <span className="text-line-arrow" aria-hidden="true">
                →
              </span>
              <input
                value={to}
                onChange={(event) => updateSearch({ to: event.target.value })}
                aria-label="Going to"
                className="w-0 flex-1 border-none bg-transparent px-3 py-[11px] text-body font-semibold outline-none"
              />
            </div>
            <Button
              variant="cta"
              size="md"
              onClick={() => results.refetch()}
              aria-label="Search"
              className="px-3.5"
            >
              <SearchIcon size={17} strokeWidth={2.2} />
            </Button>
          </div>

          <div className="mt-2 flex items-center gap-2">
            <input
              type="date"
              value={date}
              onChange={(event) => updateSearch({ date: event.target.value })}
              aria-label="Date"
              className="flex-1 rounded-xl border border-line bg-sunk px-3 py-2 text-body font-semibold outline-none"
            />
            <span className="rounded-xl border border-line bg-sunk px-3 py-2 text-body font-semibold whitespace-nowrap text-muted">
              {seats} pax
            </span>
            <Button
              variant="tealSoft"
              size="md"
              onClick={smolit.openPanel}
              aria-label="Ask Smolit to refine"
              className="px-3"
            >
              <SmolitMark size={20} />
            </Button>
          </div>
        </div>

        {/* The map is a block of its own on a phone rather than the third
            column it is on a desktop, which is the one place a phone could not
            show it at all. */}
        <div className="relative h-[38dvh] min-h-[200px] overflow-hidden">
          <SchematicMap route={MAP_ROUTES.search} height={520} />
          <div className="absolute inset-x-3 top-3 flex gap-2">
            <MapRouteChip from={from} to={to} />
          </div>
        </div>

        {/* The results, as a sheet drawn up over the map: the design system's
            own sheet shape — the 18px top radius, the upward shadow, the
            grabber. Scrolling is what expands it, so there is nothing to drag
            and nothing to get stuck half-open. */}
        <div className="relative -mt-5 rounded-t-hero border-t border-line bg-sand shadow-sheet">
          <div className="flex justify-center pt-2.5 pb-1">
            <span className="h-1 w-9 rounded-full bg-line-firm" aria-hidden="true" />
          </div>

          {/* The one bar that stays: what you need while reading the list. */}
          <div className="sticky top-header z-10 flex items-center justify-between gap-2 bg-sand px-4 py-2.5">
            <span className="text-body whitespace-nowrap text-muted">
              <strong className="text-ink">{total}</strong> rides
            </span>
            <div className="flex items-center gap-2">
              {sortButton}
              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className="flex cursor-pointer items-center gap-[7px] rounded-lg border border-line bg-surface px-3 py-2 text-label font-semibold text-ink"
              >
                Filters
                {filterCount > 0 ? (
                  <span className="rounded-full bg-ngahere-tint px-1.5 text-mini font-bold text-ngahere">
                    {filterCount}
                  </span>
                ) : null}
              </button>
            </div>
          </div>

          <div className="px-4 pt-1 pb-8">{rideList}</div>
        </div>

        <Sheet
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          placement="bottom"
          label="Filters"
          header={<span className="font-display text-lead font-bold">Filters</span>}
          footer={
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setFilters(DEFAULT_FILTERS)}
              >
                Clear
              </Button>
              <Button variant="cta" className="flex-1" onClick={() => setFiltersOpen(false)}>
                Show {total} rides
              </Button>
            </div>
          }
        >
          <div className="px-[18px] py-4">
            <FilterControls filters={filters} onChange={changeFilters} />
          </div>
        </Sheet>
      </main>
    );
  }

  /* ---------------------------------------------------------------- desktop */
  return (
    <main>
      {/* --------------------------------------------------- compact search bar */}
      <div className="sticky top-header z-20 border-b border-line bg-surface">
        <div className="mx-auto flex max-w-[1280px] flex-wrap items-center gap-2.5 px-6 py-3.5">
          <div className="flex max-w-[680px] flex-1 items-center overflow-hidden rounded-2xl border border-line bg-sunk">
            <input
              value={from}
              onChange={(event) => updateSearch({ from: event.target.value })}
              aria-label="Leaving from"
              className="flex-1 border-none bg-transparent px-3.5 py-[11px] text-body font-semibold outline-none"
            />
            <span className="text-line-arrow" aria-hidden="true">
              →
            </span>
            <input
              value={to}
              onChange={(event) => updateSearch({ to: event.target.value })}
              aria-label="Going to"
              className="flex-1 border-none bg-transparent px-3.5 py-[11px] text-body font-semibold outline-none"
            />
            <span className="h-[22px] w-px bg-line" />
            <input
              type="date"
              value={date}
              onChange={(event) => updateSearch({ date: event.target.value })}
              aria-label="Date"
              className="w-[150px] border-none bg-transparent px-3.5 py-[11px] text-body font-semibold outline-none"
            />
            <span className="h-[22px] w-px bg-line" />
            <span className="px-3.5 text-body font-semibold whitespace-nowrap text-muted">
              {seats} pax
            </span>
          </div>

          <Button
            variant="cta"
            size="md"
            onClick={() => results.refetch()}
            leadingIcon={<SearchIcon size={16} strokeWidth={2.2} />}
          >
            Search
          </Button>
          <Button
            variant="tealSoft"
            size="md"
            // Smolit is a dock over this screen, not a screen of its own. This
            // used to navigate to `/smolit`, which has redirected home since the
            // panel landed — the button went somewhere, just not where it said.
            onClick={smolit.openPanel}
            leadingIcon={<SmolitMark size={20} />}
          >
            Ask to refine
          </Button>
        </div>
      </div>

      <div className="mx-auto grid max-w-[1280px] grid-cols-1 items-start gap-5 px-6 pt-5 pb-12 lg:grid-cols-[300px_1fr] xl:grid-cols-[300px_1fr_1fr]">
        {/* ------------------------------------------------------------ filters */}
        <aside className="flex flex-col gap-[18px] lg:sticky lg:top-[140px]">
          <Card>
            <div className="mb-4 flex items-center justify-between">
              <span className="font-display text-lead font-bold">Filters</span>
              <button
                type="button"
                onClick={() => setFilters(DEFAULT_FILTERS)}
                className="cursor-pointer text-meta font-semibold text-moana-ink"
              >
                Clear
              </button>
            </div>

            <FilterControls filters={filters} onChange={changeFilters} />
          </Card>
        </aside>

        {/* --------------------------------------------------------- ride list */}
        <div>
          <div className="mb-3.5 flex items-center justify-between">
            <span className="text-body text-muted">
              <strong className="text-ink">{total}</strong> rides · {from} → {to}
            </span>
            {sortButton}
          </div>

          {rideList}
        </div>

        {/* -------------------------------------------------------------- map */}
        <div className="hidden xl:sticky xl:top-[140px] xl:block">
          <div className="relative h-[calc(100vh-168px)] min-h-[520px] overflow-hidden rounded-card border border-line">
            <SchematicMap route={MAP_ROUTES.search} height={540} />
            <div className="absolute inset-x-3 top-3 flex gap-2">
              <MapRouteChip from={from} to={to} />
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
